import { useCallback, useReducer } from 'react';
import type { ExistingSet, RecordedSet, RestType, SessionData, SessionExercise, Side, WorkoutPhase } from '../types';

export interface WorkoutState {
  phase: WorkoutPhase;
  session: SessionData | null;
  currentExerciseIndex: number;
  currentRound: number;
  currentSetNumber: number;
  currentSide: Side | null;
  restType: RestType;
  recordedSets: RecordedSet[];
  startedAt: number | null;
  adjustments: Map<string, { reps: number; weight: number }>;
}

type WorkoutAction =
  | { type: 'LOAD_SESSION'; session: SessionData }
  | { type: 'START_WORKOUT' }
  | { type: 'START_EXERCISE' }
  | { type: 'SETUP_COMPLETE' }
  | { type: 'COMPLETE_EXERCISE'; set: RecordedSet }
  | { type: 'SWITCH_SIDE' }
  | { type: 'START_REST' }
  | { type: 'END_REST' }
  | { type: 'START_ROUND_REVIEW' }
  | { type: 'START_NEXT_ROUND' }
  | { type: 'FINISH_WORKOUT' }
  | { type: 'COMPLETE_FEEDBACK' }
  | { type: 'SKIP_FEEDBACK' }
  | { type: 'ADJUST_EXERCISE'; exerciseId: string; reps: number; weight: number }
  | { type: 'RESTORE_POSITION'; session: SessionData; existingSets: ExistingSet[]; hasFeedback: boolean };

function initialState(): WorkoutState {
  return {
    phase: 'ready',
    session: null,
    currentExerciseIndex: 0,
    currentRound: 1,
    currentSetNumber: 1,
    currentSide: null,
    restType: 'exercise',
    recordedSets: [],
    startedAt: null,
    adjustments: new Map(),
  };
}

function isUnilateral(exercise: SessionExercise): boolean {
  return exercise.lateralityType === 'UnilateralSequential' || exercise.lateralityType === 'UnilateralAlternating';
}

function getNextExercisePhase(session: SessionData | null): WorkoutPhase {
  if (session && session.setupTimeSeconds > 0) return 'setup';
  return 'exercise';
}

function reducer(state: WorkoutState, action: WorkoutAction): WorkoutState {
  switch (action.type) {
    case 'LOAD_SESSION':
      return { ...state, session: action.session };

    case 'START_WORKOUT':
      return {
        ...state,
        phase: getNextExercisePhase(state.session),
        currentExerciseIndex: 0,
        currentRound: 1,
        currentSetNumber: 1,
        startedAt: Date.now(),
        currentSide: getCurrentSideForExercise(state.session, 0),
      };

    case 'START_EXERCISE':
      return {
        ...state,
        phase: 'exercise',
        currentSide: getCurrentSideForExercise(state.session, state.currentExerciseIndex),
      };

    case 'SETUP_COMPLETE':
      return {
        ...state,
        phase: 'exercise',
      };

    case 'SWITCH_SIDE':
      return {
        ...state,
        currentSide: state.currentSide === 'left' ? 'right' : 'left',
      };

    case 'COMPLETE_EXERCISE': {
      // Guard: only process if we're actually in exercise phase
      if (state.phase !== 'exercise') {
        return state;
      }

      const sets = [...state.recordedSets, action.set];
      const exercise = state.session?.exercises[state.currentExerciseIndex];

      // If unilateral sequential and just finished left side, switch to right
      if (exercise && isUnilateral(exercise) && action.set.side === 'left') {
        return { ...state, recordedSets: sets, currentSide: 'right', phase: 'exercise' };
      }

      // Check if more sets needed for this exercise (linear/hybrid mode)
      const targetSets = exercise?.targetSets ?? 1;
      if (state.currentSetNumber < targetSets) {
        // Rest between sets, then repeat this exercise
        return { ...state, recordedSets: sets, phase: 'rest', restType: 'set' };
      }

      const isLastExercise =
        state.session != null && state.currentExerciseIndex >= state.session.exercises.length - 1;
      const isLastRound = state.session != null && state.currentRound >= state.session.rounds;

      if (isLastExercise && isLastRound) {
        return { ...state, recordedSets: sets, phase: 'feedback', currentSetNumber: 1 };
      }

      if (isLastExercise) {
        return { ...state, recordedSets: sets, phase: 'round_review', currentSetNumber: 1 };
      }

      return { ...state, recordedSets: sets, phase: 'rest', restType: 'exercise', currentSetNumber: 1 };
    }

    case 'START_REST':
      return { ...state, phase: 'rest' };

    case 'END_REST': {
      // Guard: only process if we're actually in rest phase
      if (state.phase !== 'rest') {
        return state;
      }

      if (state.restType === 'set') {
        // Same exercise, next set
        return {
          ...state,
          phase: 'exercise',
          currentSetNumber: state.currentSetNumber + 1,
          currentSide: getCurrentSideForExercise(state.session, state.currentExerciseIndex),
        };
      }
      // Next exercise
      const nextIndex = state.currentExerciseIndex + 1;
      return {
        ...state,
        phase: 'exercise',
        currentExerciseIndex: nextIndex,
        currentSetNumber: 1,
        currentSide: getCurrentSideForExercise(state.session, nextIndex),
      };
    }

    case 'START_ROUND_REVIEW':
      return { ...state, phase: 'round_review' };

    case 'START_NEXT_ROUND':
      return {
        ...state,
        phase: getNextExercisePhase(state.session),
        currentExerciseIndex: 0,
        currentRound: state.currentRound + 1,
        currentSetNumber: 1,
        currentSide: getCurrentSideForExercise(state.session, 0),
      };

    case 'ADJUST_EXERCISE': {
      const newAdjustments = new Map(state.adjustments);
      newAdjustments.set(action.exerciseId, { reps: action.reps, weight: action.weight });
      return { ...state, adjustments: newAdjustments };
    }

    case 'FINISH_WORKOUT':
      return { ...state, phase: 'feedback' };

    case 'RESTORE_POSITION': {
      const { session, existingSets, hasFeedback } = action;
      const restoredSets: RecordedSet[] = existingSets.map(s => ({
        sessionExerciseId: s.sessionExerciseId,
        roundNumber: s.roundNumber,
        setNumber: s.setNumber,
        repsCompleted: s.repsCompleted,
        weightUsedKg: s.weightUsedKg,
        durationCompletedSeconds: s.durationCompletedSeconds,
        side: s.side,
        exerciseId: s.exerciseId,
        exerciseName: s.exerciseName,
        targetReps: s.targetReps,
      }));

      // Session is completed
      if (session.completedAt) {
        return {
          ...state,
          session,
          recordedSets: restoredSets,
          phase: hasFeedback ? 'complete' : 'feedback',
          startedAt: Date.now(),
        };
      }

      // Session is in progress — compute position from existing sets
      const exercises = session.exercises;
      let currentRound = 1;
      for (const s of existingSets) {
        if (s.roundNumber > currentRound) currentRound = s.roundNumber;
      }

      // Find the next incomplete exercise/set in the current round
      const roundSets = existingSets.filter(s => s.roundNumber === currentRound);
      let nextExerciseIndex = 0;
      let nextSetNumber = 1;
      let allDoneInRound = true;

      for (let i = 0; i < exercises.length; i++) {
        const ex = exercises[i];
        if (!ex) continue;
        const exSets = roundSets.filter(s => s.sessionExerciseId === ex.id);
        const uni = ex.lateralityType === 'UnilateralSequential' || ex.lateralityType === 'UnilateralAlternating';
        const sidesPerSet = uni ? 2 : 1;
        const targetSets = ex.targetSets ?? 1;
        const expectedTotal = sidesPerSet * targetSets;

        if (exSets.length < expectedTotal) {
          nextExerciseIndex = i;
          // Determine which set we're on
          nextSetNumber = Math.floor(exSets.length / sidesPerSet) + 1;
          allDoneInRound = false;
          break;
        }
      }

      if (allDoneInRound) {
        if (currentRound >= session.rounds) {
          return {
            ...state, session, recordedSets: restoredSets,
            phase: hasFeedback ? 'complete' : 'feedback',
            currentRound, startedAt: Date.now(),
          };
        }
        // Move to next round, first exercise
        currentRound++;
        nextExerciseIndex = 0;
        nextSetNumber = 1;
      }

      // Determine side for unilateral exercises
      const nextEx = exercises[nextExerciseIndex];
      // A session always has at least one exercise; bail rather than crash.
      if (!nextEx) {
        return { ...state, session, recordedSets: restoredSets, currentRound, startedAt: Date.now() };
      }
      const nextExSets = existingSets.filter(
        s => s.sessionExerciseId === nextEx.id && s.roundNumber === currentRound
      );
      const uni = nextEx.lateralityType === 'UnilateralSequential' || nextEx.lateralityType === 'UnilateralAlternating';
      let side: Side | null = null;
      if (uni) {
        // Check if left side already done for the current set number
        const currentSetSets = nextExSets.filter(s => s.setNumber === nextSetNumber);
        const hasLeft = currentSetSets.some(s => s.side === 'left');
        side = hasLeft ? 'right' : 'left';
      }

      return {
        ...state,
        session,
        recordedSets: restoredSets,
        phase: 'exercise',
        currentRound,
        currentExerciseIndex: nextExerciseIndex,
        currentSetNumber: nextSetNumber,
        currentSide: side,
        startedAt: Date.now(),
      };
    }

    case 'COMPLETE_FEEDBACK':
    case 'SKIP_FEEDBACK':
      return { ...state, phase: 'complete' };

    default:
      return state;
  }
}

function getCurrentSideForExercise(session: SessionData | null, index: number): Side | null {
  if (!session) return null;
  const exercise = session.exercises[index];
  if (!exercise) return null;
  return isUnilateral(exercise) ? 'left' : null;
}

export interface WorkoutActions {
  loadSession: (session: SessionData) => void;
  startWorkout: () => void;
  setupComplete: () => void;
  completeExercise: (set: RecordedSet) => void;
  switchSide: () => void;
  endRest: () => void;
  startNextRound: () => void;
  adjustExercise: (exerciseId: string, reps: number, weight: number) => void;
  finishWorkout: () => void;
  completeFeedback: () => void;
  skipFeedback: () => void;
  restorePosition: (session: SessionData, existingSets: ExistingSet[], hasFeedback: boolean) => void;
}

export function useWorkoutState(): WorkoutState & WorkoutActions {
  const [state, dispatch] = useReducer(reducer, undefined, initialState);

  const loadSession = useCallback((session: SessionData) => {
    dispatch({ type: 'LOAD_SESSION', session });
  }, []);

  const startWorkout = useCallback(() => {
    dispatch({ type: 'START_WORKOUT' });
  }, []);

  const setupComplete = useCallback(() => {
    dispatch({ type: 'SETUP_COMPLETE' });
  }, []);

  const completeExercise = useCallback((set: RecordedSet) => {
    dispatch({ type: 'COMPLETE_EXERCISE', set });
  }, []);

  const switchSide = useCallback(() => {
    dispatch({ type: 'SWITCH_SIDE' });
  }, []);

  const endRest = useCallback(() => {
    dispatch({ type: 'END_REST' });
  }, []);

  const startNextRound = useCallback(() => {
    dispatch({ type: 'START_NEXT_ROUND' });
  }, []);

  const adjustExercise = useCallback((exerciseId: string, reps: number, weight: number) => {
    dispatch({ type: 'ADJUST_EXERCISE', exerciseId, reps, weight });
  }, []);

  const finishWorkout = useCallback(() => {
    dispatch({ type: 'FINISH_WORKOUT' });
  }, []);

  const completeFeedback = useCallback(() => {
    dispatch({ type: 'COMPLETE_FEEDBACK' });
  }, []);

  const skipFeedback = useCallback(() => {
    dispatch({ type: 'SKIP_FEEDBACK' });
  }, []);

  const restorePosition = useCallback((session: SessionData, existingSets: ExistingSet[], hasFeedback: boolean) => {
    dispatch({ type: 'RESTORE_POSITION', session, existingSets, hasFeedback });
  }, []);

  return {
    ...state,
    loadSession,
    startWorkout,
    setupComplete,
    completeExercise,
    switchSide,
    endRest,
    startNextRound,
    adjustExercise,
    finishWorkout,
    completeFeedback,
    skipFeedback,
    restorePosition,
  };
}
